import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

/* ── Coach Timeout Tablet ──
 * Full-screen, mobile-first page with a single dramatic "TIMEOUT" button.
 * Designed for coaches on a mounted tablet — ESL-style.
 * Auth is via the 8-char token in the URL, no login needed.
 */

// Inject keyframes once
const styleId = 'coach-timeout-style';
if (!document.getElementById(styleId)) {
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes ctPulse {
      0%, 100% { box-shadow: 0 0 40px rgba(34,211,238,0.15), 0 0 80px rgba(34,211,238,0.08), inset 0 0 30px rgba(34,211,238,0.05); }
      50% { box-shadow: 0 0 60px rgba(34,211,238,0.3), 0 0 120px rgba(34,211,238,0.15), inset 0 0 50px rgba(34,211,238,0.1); }
    }
    @keyframes ctRipple {
      0% { transform: scale(1); opacity: 0.6; }
      100% { transform: scale(2.5); opacity: 0; }
    }
    @keyframes ctFadeIn {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes ctGlow {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 0.8; }
    }
    @keyframes ctCooldown {
      from { transform: scaleX(1); }
      to { transform: scaleX(0); }
    }
    @keyframes ctSuccess {
      0% { box-shadow: 0 0 40px rgba(34,197,94,0.3), 0 0 80px rgba(34,197,94,0.15); }
      50% { box-shadow: 0 0 80px rgba(34,197,94,0.5), 0 0 140px rgba(34,197,94,0.25); }
      100% { box-shadow: 0 0 40px rgba(34,197,94,0.3), 0 0 80px rgba(34,197,94,0.15); }
    }
  `;
  document.head.appendChild(style);
}

function CoachTimeout() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calling, setCalling] = useState(false);
  const [called, setCalled] = useState(false);
  const [callError, setCallError] = useState(null);
  const [cooldown, setCooldown] = useState(false);
  const pollRef = useRef(null);
  const cooldownRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`/api/matches/timeout/${token}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Invalid timeout link');
      } else {
        setError('Connection error');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    // Poll every 3 seconds for live score updates
    pollRef.current = setInterval(fetchData, 3000);
    return () => clearInterval(pollRef.current);
  }, [fetchData]);

  const handleTimeout = async () => {
    if (calling || cooldown || !data?.is_live) return;
    setCalling(true);
    setCallError(null);
    setCalled(false);
    try {
      await axios.post(`/api/matches/timeout/${token}/call`);
      setCalled(true);
      // Cooldown: prevent spam (10 seconds)
      setCooldown(true);
      cooldownRef.current = setTimeout(() => {
        setCooldown(false);
        setCalled(false);
      }, 10000);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to call timeout';
      setCallError(msg);
      setTimeout(() => setCallError(null), 4000);
    } finally {
      setCalling(false);
    }
  };

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearTimeout(cooldownRef.current);
    };
  }, []);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingText}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorBox}>
          <div style={styles.errorIcon}>✕</div>
          <div style={styles.errorText}>{error}</div>
        </div>
      </div>
    );
  }

  const isLive = data?.is_live;
  const teamName = data?.team_name || 'Unknown';
  const opponentName = data?.opponent_name || 'Unknown';
  const score1 = data?.score_team1 || 0;
  const score2 = data?.score_team2 || 0;
  const isTeam1 = data?.team_key === 'team1';

  const buttonDisabled = !isLive || calling || cooldown;

  return (
    <div style={styles.container}>
      {/* Background glow effects */}
      <div style={styles.bgGlow1} />
      <div style={styles.bgGlow2} />

      {/* Content */}
      <div style={styles.content}>
        {/* Team Name */}
        <div style={{ ...styles.teamName, animation: 'ctFadeIn 0.6s ease' }}>
          {teamName}
        </div>

        {/* Score */}
        <div style={{ ...styles.scoreRow, animation: 'ctFadeIn 0.8s ease' }}>
          <span style={{ ...styles.scoreNum, color: isTeam1 ? '#22d3ee' : '#f97316' }}>{isTeam1 ? score1 : score2}</span>
          <span style={styles.scoreDivider}>:</span>
          <span style={{ ...styles.scoreNum, color: isTeam1 ? '#f97316' : '#22d3ee' }}>{isTeam1 ? score2 : score1}</span>
        </div>

        {/* Opponent */}
        <div style={{ ...styles.vsText, animation: 'ctFadeIn 0.9s ease' }}>
          vs {opponentName}
        </div>

        {/* Status */}
        {!isLive && (
          <div style={{ ...styles.statusBadge, animation: 'ctFadeIn 1s ease' }}>
            {data?.status === 'finished' ? 'MATCH FINISHED' :
             data?.status === 'cancelled' ? 'MATCH CANCELLED' :
             data?.match_stage === 'warmup' ? 'WARMUP' :
             data?.match_stage === 'knife' ? 'KNIFE ROUND' :
             'WAITING'}
          </div>
        )}

        {/* The Big Button */}
        <div style={{ ...styles.buttonWrapper, animation: 'ctFadeIn 1.1s ease' }}>
          <button
            onClick={handleTimeout}
            disabled={buttonDisabled}
            style={{
              ...styles.button,
              ...(called ? styles.buttonCalled : {}),
              ...(buttonDisabled && !called ? styles.buttonDisabled : {}),
              animation: isLive && !called && !cooldown ? 'ctPulse 3s ease-in-out infinite' : 'none',
            }}
          >
            {/* Ripple on press */}
            {calling && <div style={styles.ripple} />}

            {/* Button inner ring */}
            <div style={{
              ...styles.buttonRing,
              borderColor: called ? 'rgba(34,197,94,0.4)' : buttonDisabled ? 'rgba(255,255,255,0.08)' : 'rgba(34,211,238,0.3)',
            }} />

            {/* Label */}
            <span style={{
              ...styles.buttonText,
              color: called ? '#22c55e' : buttonDisabled ? 'rgba(255,255,255,0.2)' : '#fff',
            }}>
              {calling ? '...' : called ? 'CALLED' : cooldown ? 'WAIT' : 'TIMEOUT'}
            </span>
          </button>

          {/* Cooldown bar */}
          {cooldown && (
            <div style={styles.cooldownBar}>
              <div style={{
                ...styles.cooldownFill,
                animation: 'ctCooldown 10s linear forwards',
              }} />
            </div>
          )}
        </div>

        {/* Error message */}
        {callError && (
          <div style={styles.callError}>{callError}</div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    position: 'fixed',
    inset: 0,
    background: '#0a0a0f',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    overflow: 'hidden',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
    touchAction: 'manipulation',
  },
  bgGlow1: {
    position: 'absolute',
    top: '-30%',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '140vw',
    height: '70vh',
    background: 'radial-gradient(ellipse, rgba(34,211,238,0.06) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  bgGlow2: {
    position: 'absolute',
    bottom: '-20%',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '120vw',
    height: '50vh',
    background: 'radial-gradient(ellipse, rgba(99,102,241,0.04) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  content: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    padding: '2rem',
    width: '100%',
    maxWidth: '500px',
  },
  teamName: {
    fontSize: 'clamp(1.2rem, 4vw, 1.8rem)',
    fontWeight: 800,
    color: '#22d3ee',
    textTransform: 'uppercase',
    letterSpacing: '0.15em',
    textAlign: 'center',
  },
  scoreRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  scoreNum: {
    fontSize: 'clamp(3rem, 12vw, 5rem)',
    fontWeight: 900,
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    lineHeight: 1,
  },
  scoreDivider: {
    fontSize: 'clamp(2rem, 8vw, 3.5rem)',
    fontWeight: 300,
    color: 'rgba(255,255,255,0.2)',
    lineHeight: 1,
  },
  vsText: {
    fontSize: 'clamp(0.8rem, 2.5vw, 1rem)',
    fontWeight: 500,
    color: 'rgba(255,255,255,0.35)',
    textTransform: 'uppercase',
    letterSpacing: '0.2em',
  },
  statusBadge: {
    fontSize: '0.75rem',
    fontWeight: 700,
    padding: '0.4rem 1.2rem',
    borderRadius: '6px',
    background: 'rgba(245,158,11,0.12)',
    color: '#f59e0b',
    border: '1px solid rgba(245,158,11,0.2)',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
  buttonWrapper: {
    marginTop: '2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
  },
  button: {
    position: 'relative',
    width: 'clamp(180px, 50vw, 260px)',
    height: 'clamp(180px, 50vw, 260px)',
    borderRadius: '50%',
    border: 'none',
    background: 'radial-gradient(circle at 40% 35%, rgba(34,211,238,0.12), rgba(15,15,25,0.95) 70%)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.15s ease, box-shadow 0.3s ease',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  buttonCalled: {
    background: 'radial-gradient(circle at 40% 35%, rgba(34,197,94,0.15), rgba(15,15,25,0.95) 70%)',
    animation: 'ctSuccess 2s ease-in-out infinite !important',
  },
  buttonDisabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
    boxShadow: 'none !important',
    animation: 'none !important',
  },
  buttonRing: {
    position: 'absolute',
    inset: '8px',
    borderRadius: '50%',
    border: '2px solid rgba(34,211,238,0.3)',
    pointerEvents: 'none',
    transition: 'border-color 0.3s ease',
  },
  buttonText: {
    position: 'relative',
    zIndex: 2,
    fontSize: 'clamp(1.4rem, 5vw, 2rem)',
    fontWeight: 900,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    transition: 'color 0.3s ease',
  },
  ripple: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    border: '2px solid rgba(34,211,238,0.4)',
    animation: 'ctRipple 0.6s ease-out',
    pointerEvents: 'none',
  },
  cooldownBar: {
    width: '80%',
    height: '3px',
    borderRadius: '2px',
    background: 'rgba(255,255,255,0.06)',
    overflow: 'hidden',
  },
  cooldownFill: {
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, #22d3ee, #6366f1)',
    borderRadius: '2px',
    transformOrigin: 'left',
  },
  callError: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#ef4444',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.2)',
    textAlign: 'center',
  },
  loadingText: {
    fontSize: '1.2rem',
    color: 'rgba(255,255,255,0.3)',
    fontWeight: 600,
    animation: 'ctGlow 2s ease-in-out infinite',
  },
  errorBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    padding: '2rem',
  },
  errorIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: 'rgba(239,68,68,0.1)',
    border: '2px solid rgba(239,68,68,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
    color: '#ef4444',
    fontWeight: 700,
  },
  errorText: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#ef4444',
    textAlign: 'center',
  },
};

export default CoachTimeout;